import React, { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import useThaklisServicesOff from '../../hooks/useThaklisServicesOff';
import {
  PLATFORM_BRAND_COLOR,
  getBrandColor,
  getBrandInk,
  getBrandTint,
  getContrastText,
  resolveImageUrl,
  withAlpha
} from '../../utils/colorUtils';
import { getTenantTitleSx } from '../../utils/titleStyle';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Collapse,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Button,
  Chip,
  Menu,
  MenuItem as MenuItemMUI,
  FormControl,
  Select,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  ConfirmationNumber as TicketIcon,
  Payment as PaymentIcon,
  Receipt as InvoiceIcon,
  RequestQuote as QuotationIcon,
  Assignment as WorkOrderIcon,
  AssignmentTurnedIn as TasksIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  BugReport as ApiTestIcon,
  LockReset as ResetPasswordIcon,
  Timeline as ActivityIcon,
  MenuBook as LedgerIcon,
  AccountBalanceWallet as WalletIcon,
  ManageAccounts as ManageAccountsIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  VpnKey as VpnKeyIcon,
  Email as EmailIcon,
  CardMembership as SubscriptionIcon,
  AccountBalanceWallet as PlatformBankIcon,
  CreditCard as GatewayIcon,
  Loyalty as PlanCatalogIcon,
  ExpandLess,
  ExpandMore,
  NoteAdd as CreateTicketIcon,
  Add as AddIcon,
  ReceiptLong as ReceiptLongIcon,
  EventBusy as DocumentExpiryIcon,
  CreditScore as CreditScoreIcon,
  Handshake as SettlementIcon,
  Inventory as CatalogIcon,
  Palette as PaletteIcon,
  Percent as TaxIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as MonetizationOnIcon,
  FactCheck as FactCheckIcon,
  SmartToy as AiAssistantIcon,
  QueryStats as AiUsageIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ThemeToggle } from '../ui/theme-toggle';
import { ServerStatus } from '../ui/server-status';
import { can, isAgentOnly } from '../../utils/roleUtils';

const drawerWidthOpen = 240;
const drawerWidthClosed = 65;

const JWT_TOKEN_KEY = 'jwt_token';

const MainLayout = ({ children, currentUser }) => {
  console.log('🏗️ [MainLayout] Rendering - currentUser:', currentUser);

  const navigate = useNavigate();
  const [newMenuAnchor, setNewMenuAnchor] = useState(null);
  const location = useLocation();
  const theme = useTheme();
  const { theme: tenantTheme, tenantName, tenantCode } = useTenant();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Mobile/tablet: < 900px

  const [drawerOpen, setDrawerOpen] = useState(!isMobile); // Closed on mobile, open on desktop
  const [selectedCustomer, setSelectedCustomer] = useState('ALL');
  const [logoutMenuAnchor, setLogoutMenuAnchor] = useState(null);

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [subscriptionMenuOpen, setSubscriptionMenuOpen] = useState(false);
  const [connectionsMenuOpen, setConnectionsMenuOpen] = useState(false);
  const [accountingMenuOpen, setAccountingMenuOpen] = useState(false);
  const [tenantsMenuOpen, setTenantsMenuOpen] = useState(false);
  const [serviceCatalogMenuOpen, setServiceCatalogMenuOpen] = useState(false);

  // Role permission helpers. Structural checks stay role-based; feature visibility is driven by the
  // RBAC matrix via can(currentUser, feature, 'read') — TC_ADMIN/platform resolve to all-true.
  const userRole = currentUser?.role;
  const isPlatformLevel = ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'].includes(userRole);
  const isPlatformAdmin = userRole === 'PLATFORM_ADMIN';
  const isTcAdmin = userRole === 'TC_ADMIN';
  const canRead = (feature) => can(currentUser, feature, 'read');
  // Thaklis-features toggle: hide Thaklis-only items when disabled.
  const thaklisDisabled = currentUser?.thaklisEnabled === false;
  const thaklisServicesOff = useThaklisServicesOff(currentUser);
  const isB2b = currentUser?.organizationType === 'B2B';
  // Client-side connections gate: promoted TCs, plus independent TCs granted the feature.
  const canConnect = currentUser?.isPromoted === true || currentUser?.connectionsEnabled === true;

  // Log route changes
  React.useEffect(() => {
    console.log('🧭 [MainLayout] Route changed to:', location.pathname);
  }, [location.pathname]);

  // Handle drawer state on screen size change
  React.useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false); // Close on mobile
    } else {
      setDrawerOpen(true); // Open on desktop
    }
  }, [isMobile]);

  React.useEffect(() => {
    if (location.pathname.startsWith('/account')) {
      setAccountMenuOpen(true);
    }
    if (location.pathname.startsWith('/configuration') ||
        location.pathname === '/email-config' ||
        location.pathname.startsWith('/settings/ai') ||
        location.pathname === '/company-config' ||
        location.pathname === '/invoice-config' ||
        location.pathname === '/work-order-config' ||
        location.pathname === '/notification-settings') {
      setConfigMenuOpen(true);
    }
    if (location.pathname.startsWith('/settings/subscription')) {
      setSubscriptionMenuOpen(true);
    }
    if (location.pathname.startsWith('/settings/connections')) {
      setConnectionsMenuOpen(true);
    }
    if (location.pathname.startsWith('/typing-centers')) {
      setTenantsMenuOpen(true);
    }
    if (location.pathname.startsWith('/service-catalog')) {
      setServiceCatalogMenuOpen(true);
    }
  }, [location.pathname]);

  // Menu items organized by sections - matching Thaklis main app design
  // PLATFORM_ADMIN: Manages TCs via Typing Centers page, no direct TC settings in sidebar
  // TC_ADMIN: Has direct access to their own TC settings in sidebar
  const menuSections = [
    // DASHBOARD - All users
    {
      title: null,
      items: [
        ...(!isAgentOnly(currentUser) ? [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
          ...(!isPlatformLevel && canRead('ai_assistant')
            ? [{ text: 'AI Assistant', icon: <AiAssistantIcon />, path: '/ai-assistant' }] : []),
        ] : []),
        ...(currentUser?.isCommissionRecipient ? [{ text: 'My Commissions', icon: <SettlementIcon />, path: '/my-commissions' }] : []),
      ]
    },
    // PLATFORM MANAGEMENT - Platform users only
    ...(isPlatformLevel ? [
      {
        title: 'PLATFORM',
        items: [
          {
            text: 'Tenants',
            icon: <BusinessIcon />,
            key: 'tenants',
            children: [
              { text: 'Typing Centers', path: '/typing-centers?type=TYPING_CENTER' },
              { text: 'PRO', path: '/typing-centers?type=PRO' },
              { text: 'Providers', path: '/typing-centers?type=B2B' }
            ]
          },
          ...(isPlatformAdmin ? [{ text: 'TC Groups', icon: <BusinessIcon />, path: '/admin/typing-center-groups' }] : []),
          ...(isPlatformAdmin ? [{ text: 'Subscriptions', icon: <SubscriptionIcon />, path: '/admin/subscriptions' }] : []),
          ...(isPlatformAdmin ? [{ text: 'Plans', icon: <PlanCatalogIcon />, path: '/admin/subscription-plans' }] : []),
          ...(isPlatformAdmin ? [{ text: 'Bank Management', icon: <PlatformBankIcon />, path: '/admin/banks' }] : []),
          ...(isPlatformAdmin ? [{ text: 'Payment Gateways', icon: <GatewayIcon />, path: '/admin/payment-gateways' }] : []),
          ...(isPlatformAdmin ? [{ text: 'AI Usage', icon: <AiUsageIcon />, path: '/admin/ai-usage' }] : []),
          { text: 'Activity', icon: <ActivityIcon />, path: '/activity' },
          ...(isPlatformAdmin ? [{ text: 'Reset TC Password', icon: <ResetPasswordIcon />, path: '/admin/reset-tc-password' }] : []),
          ...(isPlatformAdmin ? [{ text: 'API Test', icon: <ApiTestIcon />, path: '/api-test' }] : [])
        ]
      }
    ] : []),
    // Order follows the front desk's day (15-admin/NAVIGATION-ORDER.md §5): work first, then customer money, money
    // out, accounting, and set-up at the bottom. Every entry keeps its previous visibility rule.
    // WORK - TC level users only (not platform)
    ...(!isPlatformLevel ? [
      {
        title: 'WORK',
        items: [
          ...(canRead('work_order') ? [{ text: 'Work Orders', icon: <WorkOrderIcon />, path: '/work-orders' }] : []),
          // Tasks are the shared work queue — always available to any TC user.
          { text: 'Tasks', icon: <TasksIcon />, path: '/tasks' },
          ...(canRead('customer_organization') ? [{ text: 'Organizations', icon: <BusinessIcon />, path: '/organizations' }] : []),
          ...(canRead('quotation') ? [{ text: 'Quotations', icon: <QuotationIcon />, path: '/quotations' }] : []),
          // Internal Organizations sidebar item is disabled — the /internal-organizations page is not
          // ready. (It was previously hidden by a bug where organizationType was never sent; now that
          // the type IS sent, keep it hidden explicitly until the page is built.)
          // ...(currentUser?.organizationType === 'TYPING_CENTER' ? [
          //   { text: 'Internal Orgs', icon: <BusinessIcon />, path: '/internal-organizations' }
          // ] : []),
          // Tickets is a Thaklis feature — hidden when Thaklis is disabled or without access.
          // (Create Ticket moved to the "+ New" button.)
          ...((!thaklisServicesOff && canRead('thaklis_features')) ? [{ text: 'Tickets', icon: <TicketIcon />, path: '/tickets' }] : []),
          // Document Expiry is a Thaklis feature.
          ...((!thaklisDisabled && canRead('thaklis_features')) ? [{ text: 'Document Expiry', icon: <DocumentExpiryIcon />, path: '/document-expiry' }] : []),
        ]
      }
    ] : []),
    // BILLING - customer money: invoice → paid
    ...(!isPlatformLevel ? [
      {
        title: 'BILLING',
        items: [
          ...(canRead('invoice') ? [{ text: 'Invoices', icon: <InvoiceIcon />, path: '/invoices' }] : []),
          // Customer balances and top-ups (Customer Wallets / Top-Up Requests tabs).
          ...(canRead('finance') ? [{ text: 'Customer Wallets', icon: <WalletIcon />, path: '/wallets' }] : []),
          ...(canRead('finance') ? [{ text: 'Settlements', icon: <SettlementIcon />, path: '/settlements' }] : []),
          ...(canRead('finance') ? [{ text: 'Receipts', icon: <ReceiptLongIcon />, path: '/receipts' }] : []),
        ]
      }
    ] : []),
    // EXPENSES & PAYMENTS - money out
    ...(!isPlatformLevel ? [
      {
        title: 'EXPENSES & PAYMENTS',
        items: [
          ...(canRead('finance') ? [
            { text: 'Expenses', icon: <MonetizationOnIcon />, path: '/accounting/expenses' },
            { text: 'Commissions', icon: <SettlementIcon />, path: '/accounting/commissions' },
            // partner payments exist only for a client TC of a provider (connections feature)
            ...(canConnect && !isB2b ? [{ text: 'Partner Payments', icon: <SettlementIcon />, path: '/accounting/partner-payments' }] : []),
          ] : []),
          // Thaklis Payments is the TC -> Thaklis side — a thaklis_features concern.
          ...((!thaklisServicesOff && canRead('thaklis_features')) ? [{ text: 'Thaklis Payments', icon: <PaymentIcon />, path: '/payments' }] : []),
          ...(canRead('finance') ? [{ text: 'Disbursement Review', icon: <TasksIcon />, path: '/accounting/disbursement-reviews' }] : []),
        ]
      }
    ] : []),
    // ACCOUNTING - owner / accountant (`finance`; TC_ADMIN resolves all-true)
    ...((!isPlatformLevel && canRead('finance')) ? [
      {
        title: 'ACCOUNTING',
        items: [
          // Company bank accounts, cheques and credit cards (Bank Accounts / Cheques / Credit Cards tabs).
          { text: 'Banking', icon: <LedgerIcon />, path: '/banking' },
          { text: 'Transactions', icon: <LedgerIcon />, path: '/transactions' },
          { text: 'VAT Return', icon: <TaxIcon />, path: '/reports/vat-return' },
          { text: 'Health Check', icon: <FactCheckIcon />, path: '/accounting/health' },
          { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
        ]
      }
    ] : []),
    // SETTINGS - set-up and account, at the bottom
    ...(!isPlatformLevel ? [
      {
        title: 'SETTINGS',
        items: [
          // Everyone: Settings › Your account (profile, password); company sections need settings read.
          { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
          ...(isTcAdmin ? [{ text: 'Team', icon: <PeopleIcon />, path: '/team' }] : []),
          ...(canRead('service_catalog') ? [{
            text: 'Service Catalog',
            icon: <CatalogIcon />,
            key: 'serviceCatalog',
            children: [
              { text: 'Services', path: '/service-catalog' },
              { text: 'Service Mappings', path: '/service-catalog/mappings' }
            ]
          }] : []),
          // Subscription - gated by the subscription feature (TC_ADMIN resolves all-true)
          ...(canRead('subscription') ? [
            { text: 'Subscription', icon: <SubscriptionIcon />, path: '/settings/subscription' }
          ] : []),
          // Connections — connections-enabled TCs (clients of providers) and Providers (B2Bs), gated by the feature
          ...(((canConnect || isB2b) && canRead('connections')) ? [
            {
              text: 'Connections',
              icon: <BusinessIcon />,
              key: 'connections',
              children: isB2b
                ? [
                    { text: 'Clients', path: '/settings/connections/clients' },
                    { text: 'Inquiries', path: '/settings/connections/inquiries' },
                    { text: 'Discovery', path: '/settings/connections/discovery' },
                  ]
                : [
                    { text: 'Providers', path: '/settings/connections/providers' },
                    { text: 'Inquiries', path: '/settings/connections/inquiries' },
                  ],
            }
          ] : []),
          // Audit log — kept visible to every TC user, as before.
          { text: 'Activity Log', icon: <ActivityIcon />, path: '/activity' },
          // My Account lives in Settings › Your account and the avatar menu (top right).
        ]
      }
    ] : [])
  ]
    // Drop sections a user can see no items in (e.g. a staff member with no billing permissions
    // shouldn't see an empty BILLING header).
    .filter((section) => (section.items || []).length > 0);

  // "+ New" — one create button at the top of the sidebar (replaces the "Create Ticket" menu entry).
  const createActions = isPlatformLevel ? [] : [
    can(currentUser, 'work_order', 'write') && { text: 'Work Order', icon: <WorkOrderIcon fontSize="small" />, path: '/work-orders/create' },
    can(currentUser, 'quotation', 'write') && { text: 'Quotation', icon: <QuotationIcon fontSize="small" />, path: '/quotations/create' },
    (!thaklisServicesOff && can(currentUser, 'thaklis_features', 'write')) && { text: 'Ticket', icon: <CreateTicketIcon fontSize="small" />, path: '/tickets/create' },
    can(currentUser, 'finance', 'write') && { text: 'Expense', icon: <MonetizationOnIcon fontSize="small" />, path: '/accounting/expenses/new' },
  ].filter(Boolean);

  // Determine typing center info based on user role
  const getRoleLabel = () => {
    switch (userRole) {
      case 'PLATFORM_ADMIN': return 'Platform Admin';
      case 'PLATFORM_SUPPORT': return 'Support';
      case 'TC_ADMIN': return 'Admin';
      case 'TC_STAFF': return currentUser?.roleName || 'Staff';
      default: return userRole;
    }
  };

  // Sidebar identity, resolved once:
  //   tenant light  -> the exact selected Brand Color
  //   platform      -> fixed platform colour (never tenant-driven)
  //   dark          -> fixed dark chrome
  // A group owner acting inside a typing center arrives with role TC_ADMIN and
  // an acting tenant config, so they correctly take the tenant branch.
  const sidebarBackground = theme.palette.mode === 'dark'
    ? '#0d1117'
    : (isPlatformLevel ? PLATFORM_BRAND_COLOR : getBrandColor(theme));

  // Everything painted ON the sidebar follows the resolved background rather
  // than assuming white: a light Brand Color needs near-black labels.
  const sidebarOnColor = getContrastText(sidebarBackground);
  const onSidebar = (opacity) => withAlpha(sidebarOnColor, opacity);

  const typingCenterInfo = isPlatformLevel
    ? { name: 'All Centers', code: getRoleLabel() }
    : { name: currentUser?.typingCenterName || tenantName, code: tenantCode };

  // Tenant title typography — the sidebar and top-bar tenant marks only.
  // Platform-level shells keep fixed platform typography, so the style is an
  // empty fragment for them. A group owner acting inside a typing center
  // arrives as TC_ADMIN with that centre's config, so they get its style.
  // Font size stays with each consumer; this supplies character only.
  const tenantTitleSx = isPlatformLevel ? {} : getTenantTitleSx(tenantTheme?.titleStyle);

  const handleLogout = () => {
    console.log('🚪 [MainLayout] Logout clicked - clearing the staff session and navigating to /login');
    setLogoutMenuAnchor(null);
    // Clear only the staff session. Client-portal and customer-group sessions in the same browser
    // belong to other users and must survive a TC logout.
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    // A group owner who opened a TC from their group is logging out of their whole session.
    if (localStorage.getItem('actingAsGroupOwner') === 'true') {
      localStorage.removeItem('groupAuthenticated');
      localStorage.removeItem('groupToken');
      localStorage.removeItem('groupUser');
      localStorage.removeItem('groupData');
    }
    localStorage.removeItem('actingAsGroupOwner');
    // Clear any cached data
    sessionStorage.clear();
    // Force page reload to clear all React state
    window.location.href = '/login';
  };

  // When a group owner has "opened" a TC, offer a way back to their list.
  // The acting token and current-app auth are cleared; the group token is
  // preserved so they return to /group/centers without logging in again.
  const actingAsGroupOwner = localStorage.getItem('actingAsGroupOwner') === 'true';

  const handleBackToCenters = () => {
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('actingAsGroupOwner');
    sessionStorage.clear();
    window.location.href = '/group/centers';
  };

  const isActive = (path) => {
    if (path === '/typing-centers?type=TYPING_CENTER') {
      return location.pathname.startsWith('/typing-centers') && location.search.includes('type=TYPING_CENTER');
    }
    if (path === '/typing-centers?type=PRO') {
      return location.pathname.startsWith('/typing-centers') && location.search.includes('type=PRO');
    }
    if (path === '/typing-centers') {
      return location.pathname.startsWith('/typing-centers');
    }
    if (path === '/ai-assistant') {
      return location.pathname.startsWith('/ai-assistant');
    }
    if (path === '/wallets') {
      return location.pathname.startsWith('/wallets');
    }
    if (path === '/banking') {
      return location.pathname.startsWith('/banking') || location.pathname.startsWith('/accounting/credit-cards');
    }
    if (path === '/invoices') {
      return (location.pathname.startsWith('/invoices') && !location.pathname.startsWith('/invoices/received')) || location.pathname === '/invoice-config';
    }
    if (path === '/quotations') {
      return location.pathname.startsWith('/quotations') && !location.pathname.startsWith('/quotations/received');
    }
    if (path === '/quotations/received') {
      return location.pathname.startsWith('/quotations/received');
    }
    if (path === '/invoices/received') {
      return location.pathname.startsWith('/invoices/received');
    }
    if (path === '/work-orders') {
      return location.pathname.startsWith('/work-orders') && !location.pathname.startsWith('/work-orders/received');
    }
    if (path === '/work-orders/received') {
      return location.pathname.startsWith('/work-orders/received');
    }
    if (path === '/accounting/health') {
      return location.pathname === '/accounting/health';
    }
    if (path === '/accounting/expenses') {
      return location.pathname.startsWith('/accounting/expenses');
    }
    if (path === '/accounting/credit-cards') {
      return location.pathname.startsWith('/accounting/credit-cards');
    }
    if (path === '/accounting/commissions') {
      return location.pathname.startsWith('/accounting/commissions');
    }
    if (path === '/accounting/drawings') {
      return location.pathname === '/accounting/drawings';
    }
    if (path === '/accounting/reconciliation') {
      return location.pathname.startsWith('/accounting/reconciliation');
    }
    if (path === '/accounting/journal-entries') {
      return location.pathname.startsWith('/accounting/journal-entries');
    }
    if (path === '/accounting/assets') {
      return location.pathname === '/accounting/assets';
    }
    if (path === '/reports/profit-loss') {
      return location.pathname === '/reports/profit-loss';
    }
    if (path === '/reports/balance-sheet') {
      return location.pathname === '/reports/balance-sheet';
    }
    if (path === '/reports/trial-balance') {
      return location.pathname === '/reports/trial-balance';
    }
    if (path === '/reports/general-ledger') {
      return location.pathname === '/reports/general-ledger';
    }
    if (path === '/reports/vat-return') {
      return location.pathname === '/reports/vat-return';
    }
    if (path === '/reports/client-ledger') {
      return location.pathname.startsWith('/reports/client-ledger');
    }
    if (path === '/reports') {
      return location.pathname === '/reports';
    }
    if (path === '/admin/subscriptions') {
      return location.pathname === '/admin/subscriptions';
    }
    if (path === '/settings/subscription') {
      return location.pathname.startsWith('/settings/subscription');
    }
    return location.pathname === path;
  };

  const isGroupActive = (children) => {
    return Array.isArray(children) && children.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path));
  };

  const isGroupOpen = (key) => {
    if (key === 'account') return accountMenuOpen;
    if (key === 'configuration') return configMenuOpen;
    if (key === 'subscription') return subscriptionMenuOpen;
    if (key === 'connections') return connectionsMenuOpen;
    if (key === 'tenants') return tenantsMenuOpen;
    if (key === 'serviceCatalog') return serviceCatalogMenuOpen;
    return false;
  };

  const toggleGroupOpen = (key) => {
    if (key === 'account') setAccountMenuOpen((v) => !v);
    if (key === 'configuration') setConfigMenuOpen((v) => !v);
    if (key === 'subscription') setSubscriptionMenuOpen((v) => !v);
    if (key === 'connections') setConnectionsMenuOpen((v) => !v);
    if (key === 'tenants') setTenantsMenuOpen((v) => !v);
    if (key === 'serviceCatalog') setServiceCatalogMenuOpen((v) => !v);
  };

  // Route-level feature guard (defence-in-depth beside the sidebar + backend). A TC_STAFF user who
  // URL-hops to a feature they lack is redirected to the dashboard; TC_ADMIN/platform resolve all-true.
  // Longest paths first so /work-order-config isn't shadowed by /work-order, etc.
  const ROUTE_FEATURE = [
    ['/service-catalog', 'service_catalog'],
    ['/organizations', 'customer_organization'],
    ['/work-order-config', 'settings'],
    ['/work-orders', 'work_order'],
    ['/quotations', 'quotation'],
    ['/invoice-config', 'settings'],
    ['/invoices', 'invoice'],
    ['/wallets', 'finance'],
    ['/banking', 'finance'],
    ['/receipts', 'finance'],
    ['/settlements', 'finance'],
    ['/transactions', 'finance'],
    ['/tax', 'finance'],
    ['/payments/cheques', 'finance'],
    ['/payments', 'thaklis_features'],
    ['/tickets', 'thaklis_features'],
    ['/document-expiry', 'thaklis_features'],
    ['/settings/subscription', 'subscription'],
    ['/settings/connections', 'connections'],
    ['/settings/tenant', 'settings'],
    ['/company-config', 'settings'],
    ['/email-config', 'settings'],
    ['/notification-settings', 'settings'],
    ['/ai-config', 'settings'],
    ['/settings/ai', 'settings'],
    ['/ai-assistant/usage', 'settings'],
    ['/ai-assistant', 'ai_assistant'],
    // P&L Accounting (25-pl-accounting/ROUTES.md "Permission Mapping"): every accounting page,
    // every report and the accounting settings pages sit under `finance`.
    ['/accounting', 'finance'],
    ['/reports', 'finance'],
    ['/settings/vat', 'finance'],
    ['/settings/expense-categories', 'finance'],
    ['/settings/asset-config', 'finance'],
    ['/settings/service-providers', 'settings'],
  ];
  const guarded = ROUTE_FEATURE.find(
    ([p]) => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (guarded && !can(currentUser, guarded[1], 'read')) {
    return <Navigate to="/dashboard" replace />;
  }
  // The AI chat and the AI Usage page show typing-center data; platform users get /admin/ai-usage instead
  if (isPlatformLevel && (location.pathname.startsWith('/ai-assistant') || location.pathname.startsWith('/settings/ai/usage'))) {
    return <Navigate to="/dashboard" replace />;
  }
  // Owner drawings and manual journal entries are TC_ADMIN-only on top of `finance`
  // (ROUTES.md: "write + TC_ADMIN only") — finance-role staff must not reach them.
  const ADMIN_ONLY_ROUTES = ['/accounting/drawings', '/accounting/journal-entries/new', '/settings/roles'];
  const adminOnly = ADMIN_ONLY_ROUTES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (adminOnly && !isTcAdmin && !isPlatformLevel) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => isMobile && setDrawerOpen(false)}
        sx={{
          width: isMobile ? drawerWidthOpen : (drawerOpen ? drawerWidthOpen : drawerWidthClosed),
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? drawerWidthOpen : (drawerOpen ? drawerWidthOpen : drawerWidthClosed),
            boxSizing: 'border-box',
            backgroundColor: sidebarBackground,
            color: sidebarOnColor,
            display: 'flex',
            flexDirection: 'column',
            '& .MuiListItemText-primary, & .MuiListItemIcon-root, & .MuiSvgIcon-root': {
              color: `${sidebarOnColor} !important`
            },
            borderTopRightRadius: isMobile ? 0 : 15,
            borderBottomRightRadius: isMobile ? 0 : 15,
            transition: 'width 0.2s',
            overflowX: 'hidden'
          },
        }}
      >
        {/* Unified header: profile image (user → company logo fallback),
            user name, company name, and the collapse toggle. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: (drawerOpen || isMobile) ? 'row' : 'column',
            alignItems: 'center',
            gap: 1.5,
            px: (drawerOpen || isMobile) ? 2 : 1,
            pt: 2,
            pb: 1.5,
            minHeight: 64
          }}
        >
          <Avatar
            src={
              resolveImageUrl(currentUser?.profileImageUrl)
              || (!isPlatformLevel && currentUser?.logoDataUrl)
              || (!isPlatformLevel && resolveImageUrl(tenantTheme?.logoUrl))
              || undefined
            }
            sx={{
              width: 44,
              height: 44,
              bgcolor: onSidebar(0.20),
              border: `2px solid ${onSidebar(0.20)}`,
              fontWeight: 600,
              fontSize: '1rem',
              color: sidebarOnColor,
              flexShrink: 0,
              '& img': { objectFit: 'contain' }
            }}
          >
            {(currentUser?.name || 'U').charAt(0).toUpperCase()}
          </Avatar>
          {(drawerOpen || isMobile) && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: sidebarOnColor,
                  fontSize: '0.9rem',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {currentUser?.name || 'User'}
              </Typography>
              {/* Tenant brand title — carries the configured Title Style.
                  The user name above and every nav label below keep the
                  application font. */}
              <Typography
                sx={{
                  ...tenantTitleSx,
                  color: onSidebar(0.70),
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {tenantName || currentUser?.typingCenterName || typingCenterInfo.name}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider sx={{ borderColor: onSidebar(0.12) }} />

        {/* + New */}
        {createActions.length > 0 && (
          <Box sx={{ px: 1.5, pt: 1.5 }}>
            {(drawerOpen || isMobile) ? (
              <Button
                fullWidth
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => setNewMenuAnchor(e.currentTarget)}
                sx={{
                  justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600,
                  color: sidebarOnColor, bgcolor: onSidebar(0.14), '&:hover': { bgcolor: onSidebar(0.22) }, py: 0.75, px: 1.5,
                }}
              >
                New
              </Button>
            ) : (
              <Tooltip title="New" placement="right">
                <IconButton size="small" onClick={(e) => setNewMenuAnchor(e.currentTarget)}
                  sx={{ color: sidebarOnColor, bgcolor: onSidebar(0.14), '&:hover': { bgcolor: onSidebar(0.22) } }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Menu anchorEl={newMenuAnchor} open={Boolean(newMenuAnchor)} onClose={() => setNewMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
              {createActions.map((a) => (
                <MenuItemMUI key={a.path} onClick={() => { setNewMenuAnchor(null); navigate(a.path); if (isMobile) setDrawerOpen(false); }}
                  sx={{ fontSize: '0.8125rem', gap: 1, minWidth: 180 }}>
                  {a.icon}{a.text}
                </MenuItemMUI>
              ))}
            </Menu>
          </Box>
        )}

        {/* Menu Items - Thaklis Style */}
        <Box sx={{ pt: 1, px: 1 }}>
          {menuSections.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {/* Section Header */}
              {section.title && (drawerOpen || isMobile) && (
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: onSidebar(0.55),
                    letterSpacing: '0.08em',
                    px: 1.5,
                    pt: sectionIndex === 0 ? 1 : 2,
                    pb: 1
                  }}
                >
                  {section.title}
                </Typography>
              )}

              {/* Section Items */}
              <List disablePadding>
                {section.items.map((item) => {
                  const groupActive = item.children ? isGroupActive(item.children) : false;
                  const groupOpen = item.children ? isGroupOpen(item.key) : false;
                  const itemActive = !item.children && isActive(item.path);

                  if (item.children) {
                    return (
                      <React.Fragment key={item.text}>
                        <ListItem disablePadding sx={{ display: 'block', mb: 0.3 }}>
                          <ListItemButton
                            onClick={() => toggleGroupOpen(item.key)}
                            sx={{
                              minHeight: 38,
                              justifyContent: (drawerOpen || isMobile) ? 'initial' : 'center',
                              px: 1.5,
                              py: 0.8,
                              borderRadius: '8px',
                              backgroundColor: groupActive ? onSidebar(0.16) : 'transparent',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                backgroundColor: onSidebar(0.10)
                              }
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: (drawerOpen || isMobile) ? 1.5 : 'auto',
                                justifyContent: 'center',
                                color: sidebarOnColor,
                                opacity: 0.9,
                                '& .MuiSvgIcon-root': { fontSize: '1.125rem' }
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            {(drawerOpen || isMobile) && (
                              <ListItemText
                                primary={item.text}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: sidebarOnColor,
                                    fontWeight: groupActive ? 600 : 400,
                                    fontSize: '0.8125rem'
                                  }
                                }}
                              />
                            )}
                            {(drawerOpen || isMobile) && (
                              <Box sx={{ color: onSidebar(0.60) }}>
                                {groupOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              </Box>
                            )}
                          </ListItemButton>
                        </ListItem>

                        <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {item.children.map((child) => {
                              const childActive = location.pathname === child.path ||
                                (child.path === '/quotations' && location.pathname.startsWith('/quotations') && !location.pathname.startsWith('/quotations/received')) ||
                                (child.path === '/invoices' && location.pathname.startsWith('/invoices') && !location.pathname.startsWith('/invoices/received')) ||
                                (child.path === '/work-orders' && location.pathname.startsWith('/work-orders') && !location.pathname.startsWith('/work-orders/received')) ||
                                (child.path === '/work-orders/received' && location.pathname.startsWith('/work-orders/received'));
                              return (
                                <ListItem key={child.text} disablePadding sx={{ display: 'block' }}>
                                  <ListItemButton
                                    onClick={() => {
                                      navigate(child.path);
                                      if (isMobile) setDrawerOpen(false);
                                    }}
                                    sx={{
                                      minHeight: 34,
                                      pl: (drawerOpen || isMobile) ? 5.5 : 1.5,
                                      pr: 1.5,
                                      py: 0.5,
                                      borderRadius: '8px',
                                      backgroundColor: childActive ? onSidebar(0.10) : 'transparent',
                                      transition: 'all 0.15s ease',
                                      '&:hover': {
                                        backgroundColor: onSidebar(0.08)
                                      }
                                    }}
                                  >
                                    {(drawerOpen || isMobile) && (
                                      <ListItemText
                                        primary={child.text}
                                        sx={{
                                          '& .MuiListItemText-primary': {
                                            color: sidebarOnColor,
                                            fontWeight: childActive ? 500 : 400,
                                            fontSize: '0.78rem',
                                            opacity: childActive ? 1 : 0.85
                                          }
                                        }}
                                      />
                                    )}
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  }

                  return (
                    <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.3 }}>
                      <ListItemButton
                        onClick={() => {
                          navigate(item.path);
                          if (isMobile) setDrawerOpen(false);
                        }}
                        sx={{
                          minHeight: 38,
                          justifyContent: (drawerOpen || isMobile) ? 'initial' : 'center',
                          px: 1.5,
                          py: 0.8,
                          borderRadius: '8px',
                          backgroundColor: itemActive ? onSidebar(0.16) : 'transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: onSidebar(0.10)
                          }
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: (drawerOpen || isMobile) ? 1.5 : 'auto',
                            justifyContent: 'center',
                            color: sidebarOnColor,
                            opacity: 0.9,
                            '& .MuiSvgIcon-root': { fontSize: '1.125rem' }
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {(drawerOpen || isMobile) && (
                          <ListItemText
                            primary={item.text}
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: sidebarOnColor,
                                fontWeight: itemActive ? 600 : 400,
                                fontSize: '0.8125rem'
                              }
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </React.Fragment>
          ))}
        </Box>

        {/* Powered by Thaklis - tenant only */}
        {!isPlatformLevel && (
          <Box sx={{ mt: 'auto', p: 1.5, textAlign: 'center', borderTop: `1px solid ${onSidebar(0.12)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
              <img src="/thaklis-logo.png" alt="Thaklis" style={{ width: 20, height: 20, objectFit: 'contain' }} />
              {(drawerOpen || isMobile) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: onSidebar(0.40), lineHeight: 1.2, letterSpacing: '0.05em' }}>
                    Powered by
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: onSidebar(0.60), lineHeight: 1.2 }}>
                    Thaklis
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        {/* AppBar */}
        <AppBar
          position="fixed"
          sx={{
            width: isMobile ? '100%' : `calc(100% - ${drawerOpen ? drawerWidthOpen : drawerWidthClosed}px)`,
            ml: isMobile ? 0 : `${drawerOpen ? drawerWidthOpen : drawerWidthClosed}px`,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1,
            transition: 'width 0.2s, margin 0.2s'
          }}
        >
          <Toolbar>
            {/* Sidebar toggle — a directional chevron: < to collapse/close when open, > to
                expand/open when collapsed. The single control for the sidebar on all viewports. */}
            <IconButton
              color="inherit"
              aria-label={drawerOpen ? 'collapse sidebar' : 'expand sidebar'}
              edge="start"
              onClick={() => setDrawerOpen((prev) => !prev)}
              sx={{ mr: 2, color: 'primary.main' }}
            >
              {drawerOpen ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
            <Typography variant="h6" component="div" noWrap sx={{ flexGrow: 0, mr: 2, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' }, maxWidth: { xs: 150, sm: 'none' } }}>
              {location.pathname === '/dashboard' && 'Dashboard'}
              {location.pathname === '/organizations' && 'Organizations'}
              {location.pathname.startsWith('/wallets') && 'Wallets'}
              {location.pathname.startsWith('/banking') && 'Banking'}
              {location.pathname === '/tickets' && 'Tickets'}
              {location.pathname === '/activity' && 'Activity'}
              {location.pathname === '/document-expiry' && 'Document Expiry'}
              {location.pathname === '/payments' && 'Thaklis Payments'}
              {location.pathname === '/transactions' && 'Transaction Ledger'}
              {location.pathname === '/quotations/create' && 'New Quotation'}
              {/^\/quotations\/[^/]+\/edit$/.test(location.pathname) && 'Edit Quotation'}
              {(location.pathname.startsWith('/quotations') && !location.pathname.startsWith('/quotations/received')
                && location.pathname !== '/quotations/create' && !/^\/quotations\/[^/]+\/edit$/.test(location.pathname)) && 'Quotations (Sent)'}
              {location.pathname.startsWith('/quotations/received') && 'Quotations (Received)'}
              {(location.pathname.startsWith('/invoices') && !location.pathname.startsWith('/invoices/received')) && 'Invoices (Sent)'}
              {location.pathname.startsWith('/invoices/received') && 'Invoices (Received)'}
              {location.pathname === '/invoice-config' && 'Invoice Settings'}
              {location.pathname === '/work-order-config' && 'Work Order Settings'}
              {location.pathname === '/email-config' && 'Email Configuration'}
              {location.pathname === '/settings/ai' && 'AI Settings'}
              {location.pathname === '/ai-assistant/usage' && 'AI Usage'}
              {location.pathname === '/notification-settings' && 'Notification Settings'}
              {location.pathname === '/company-config' && 'Company Configuration'}
              {location.pathname === '/admin/reset-tc-password' && 'Reset TC Password'}
              {location.pathname === '/api-test' && 'API Test'}
              {(location.pathname.startsWith('/typing-centers') && isPlatformLevel && location.search.includes('type=TYPING_CENTER')) && 'Typing Centers'}
              {(location.pathname.startsWith('/typing-centers') && isPlatformLevel && location.search.includes('type=PRO')) && 'PRO'}
              {(location.pathname.startsWith('/typing-centers') && isPlatformLevel && !location.search.includes('type=')) && 'Tenants'}
              {location.pathname === '/account/profile' && 'My Profile'}
              {location.pathname === '/account/change-password' && 'Change Password'}
              {(location.pathname.startsWith('/work-orders') && !location.pathname.startsWith('/work-orders/received')) && 'Work Orders (Sent)'}
              {location.pathname.startsWith('/work-orders/received') && 'Work Orders (Received)'}
              {location.pathname === '/settings/subscription' && 'Subscription'}
              {location.pathname === '/settings/subscription/plans' && 'Choose Plan'}
              {location.pathname === '/settings/subscription/usage' && 'Usage & Limits'}
              {location.pathname === '/settings/subscription/billing' && 'Billing History'}
              {location.pathname === '/settings/connections/providers' && 'Providers'}
              {location.pathname === '/settings/connections/clients' && 'Clients'}
              {location.pathname === '/settings/connections/inquiries' && 'Inquiries'}
              {location.pathname === '/settings/connections/discovery' && 'Discovery'}
              {location.pathname === '/admin/subscriptions' && 'Subscriptions'}
              {location.pathname === '/admin/typing-center-groups' && 'TC Groups'}
              {location.pathname === '/admin/ai-usage' && 'Platform AI Usage'}
              {location.pathname.startsWith('/receipts') && 'Receipts'}
              {location.pathname.startsWith('/settlements') && 'Settlements'}
              {location.pathname === '/service-catalog' && 'Service Catalog'}
              {location.pathname === '/service-catalog/mappings' && 'Service Mappings'}
              {location.pathname === '/settings' && 'Settings'}
              {location.pathname === '/account' && 'My Account'}
              {location.pathname === '/settings/subscription' && 'Subscription'}
              {location.pathname === '/tasks' && 'Tasks'}
              {location.pathname === '/team' && 'Team'}
              {location.pathname === '/internal-organizations' && 'Internal Organizations'}
              {location.pathname === '/invoices/create-from-work-orders' && 'Invoice from Work Orders'}
              {location.pathname === '/settings/tenant' && (isPlatformAdmin ? 'Tenant Settings' : 'Branding Settings')}
              {location.pathname === '/accounting/health' && 'Financial Health Check'}
              {location.pathname === '/accounting/expenses' && 'Expenses'}
              {location.pathname === '/accounting/expenses/new' && 'New Expense'}
              {location.pathname.startsWith('/accounting/expenses/') && location.pathname.endsWith('/edit') && 'Edit Expense'}
              {location.pathname.startsWith('/accounting/expenses/') && location.pathname !== '/accounting/expenses/new' && !location.pathname.endsWith('/edit') && 'Expense Detail'}
              {location.pathname === '/accounting/commissions' && 'Commissions'}
              {location.pathname === '/accounting/commissions/config' && 'Commission Config'}
              {location.pathname === '/accounting/commissions/recipients' && 'Commission Recipients'}
              {location.pathname === '/accounting/credit-cards' && 'Credit Cards'}
              {location.pathname === '/payments/cheques' && 'Post-Dated Cheques'}
              {location.pathname === '/accounting/partner-payments' && 'Partner Payments'}
              {location.pathname === '/my-commissions' && 'My Commissions'}
              {location.pathname === '/settings/roles' && 'Roles'}
              {location.pathname === '/accounting/disbursement-reviews' && 'Disbursement Review'}
              {location.pathname.startsWith('/accounting/credit-cards/') && 'Credit Card Detail'}
              {location.pathname === '/accounting/drawings' && 'Owner Drawings'}
              {location.pathname === '/accounting/partners' && 'Partners'}
              {location.pathname === '/accounting/reconciliation' && 'Reconciliation'}
              {location.pathname === '/reports/cash-flow' && 'Cash Flow'}
              {location.pathname === '/reports/cash-position' && 'Cash Position'}
              {location.pathname === '/reports/receivables-aging' && 'Receivables Aging'}
              {location.pathname === '/reports/invoice-report' && 'Invoice Report'}
              {location.pathname === '/reports/service-profit' && (new URLSearchParams(location.search).get('sort') === 'VOLUME' ? 'Service Mix' : 'Profit Report')}
              {location.pathname === '/reports/agent-performance' && 'Agent Performance'}
              {location.pathname === '/reports/staff-commission' && 'Staff Commission'}
              {location.pathname === '/reports/salary' && 'Salary Report'}
              {location.pathname === '/reports/suppliers' && 'Supplier Report'}
              {location.pathname === '/reports/daily-sheet' && 'Daily Sheet'}
              {location.pathname === '/reports/inactive-clients' && 'Inactive Clients'}
              {location.pathname === '/reports/annual-pl' && 'Annual P&L'}
              {location.pathname === '/reports/expenses' && 'Expense Report'}
              {location.pathname === '/accounting/reconciliation/new' && 'New Reconciliation'}
              {/^\/accounting\/reconciliation\/(?!new$)[^/]+$/.test(location.pathname) && 'Reconciliation'}
              {location.pathname === '/accounting/journal-entries' && 'Journal Entries'}
              {location.pathname === '/accounting/journal-entries/new' && 'New Journal Entry'}
              {location.pathname.startsWith('/accounting/journal-entries/') && location.pathname !== '/accounting/journal-entries/new' && 'Journal Entry'}
              {location.pathname === '/accounting/assets' && 'Fixed Assets'}
              {location.pathname === '/reports' && 'Reports'}
              {location.pathname === '/reports/profit-loss' && 'Profit & Loss'}
              {location.pathname === '/reports/balance-sheet' && 'Balance Sheet'}
              {location.pathname === '/reports/trial-balance' && 'Trial Balance'}
              {location.pathname === '/reports/general-ledger' && 'General Ledger'}
              {location.pathname === '/reports/vat-return' && 'VAT Return'}
              {location.pathname === '/reports/client-ledger' && 'Client Statements'}
              {location.pathname.startsWith('/reports/client-ledger/') && 'Client Statement'}
              {location.pathname === '/settings/vat' && 'VAT Settings'}
              {location.pathname === '/settings/expense-categories' && 'Expense Categories'}
              {location.pathname === '/settings/service-providers' && 'Service Providers'}
              {location.pathname === '/settings/asset-config' && 'Asset Settings'}
            </Typography>

            {!isMobile && (
              <>
                <Chip
                  label={typingCenterInfo.code ? `${typingCenterInfo.name} (${typingCenterInfo.code})` : typingCenterInfo.name}
                  color={isPlatformLevel ? 'error' : 'primary'}
                  variant="outlined"
                  size="small"
                  // Tenant brand mark in the top bar. Title Style lands on the
                  // label only, so the chip keeps its own size and spacing and
                  // no other toolbar control is affected.
                  sx={{
                    mr: 1,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '& .MuiChip-label': tenantTitleSx
                  }}
                />
                {!isPlatformLevel && (
                  <Chip
                    label={currentUser?.organizationType === 'TYPING_CENTER' ? 'Typing Center' : 'PRO'}
                    size="small"
                    sx={{
                      mr: 2,
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      backgroundColor: 'action.hover',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
              </>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Header Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Server Status Indicator */}
              <ServerStatus />

              {/* Dark Mode Toggle */}
              <ThemeToggle />

              {/* Notifications */}
              <Tooltip title="Notifications" arrow>
                <IconButton
                  size="small"
                  sx={{
                    color: '#64748b',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                    }
                  }}
                >
                  <NotificationsIcon />
                </IconButton>
              </Tooltip>

              {/* Account menu: who is signed in, My Account, Log out */}
              <Tooltip title="Account" arrow>
                <IconButton size="small" onClick={(e) => setLogoutMenuAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                  <Avatar
                    src={resolveImageUrl(currentUser?.profileImageUrl) || undefined}
                    sx={{ width: 30, height: 30, fontSize: '0.8125rem', bgcolor: 'primary.main', color: 'primary.contrastText' }}
                  >
                    {(currentUser?.name || currentUser?.username || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>

            <Menu
              anchorEl={logoutMenuAnchor}
              open={Boolean(logoutMenuAnchor)}
              onClose={() => setLogoutMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 240, mt: 0.5 } }}
              MenuListProps={{ dense: true, sx: { py: 0.5 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, pt: 1, pb: 1.25 }}>
                <Avatar
                  src={resolveImageUrl(currentUser?.profileImageUrl) || undefined}
                  sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main', color: 'primary.contrastText' }}
                >
                  {(currentUser?.name || currentUser?.username || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>{currentUser?.name || currentUser?.username || 'User'}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>{currentUser?.email}</Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }} noWrap>
                    {[tenantName || currentUser?.typingCenterName, getRoleLabel()].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItemMUI onClick={() => { setLogoutMenuAnchor(null); navigate('/settings?section=account'); }} sx={{ fontSize: '0.8125rem', minHeight: 34 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><ManageAccountsIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                My account & password
              </MenuItemMUI>
              <MenuItemMUI onClick={handleLogout} sx={{ fontSize: '0.8125rem', minHeight: 34, color: 'error.main' }}>
                <ListItemIcon sx={{ minWidth: 30, color: 'error.main' }}><LogoutIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                Log out
              </MenuItemMUI>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            mt: 8,
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
            overflow: 'hidden'
          }}
        >
          {/* Group-owner "acting" banner — shown while an owner has opened a TC */}
          {actingAsGroupOwner && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2,
                px: 2,
                py: 1,
                borderRadius: 1,
                backgroundColor: getBrandTint(theme, 0.08),
                border: '1px solid',
                // Border sits on a pale tint, so it takes BRAND INK to stay visible.
                borderColor: getBrandInk(theme),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Acting as {currentUser?.typingCenterName || 'this typing center'}
              </Typography>
              <Button size="small" variant="outlined" onClick={handleBackToCenters}>
                ← Back to my centers
              </Button>
            </Box>
          )}
          {/* Pass selected customer and currentUser to children */}
          {React.cloneElement(children, { selectedCustomer, currentUser })}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
